import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, Button } from '@mui/material';
import { Check as CheckIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const NotificationPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Chỉ fetch notifications lần đầu khi component mount
    fetchNotifications();
    
    // Lắng nghe custom event để fetch notifications khi có trigger
    const handleNotificationTrigger = () => {
      console.log('📢 Notification trigger received - fetching notifications');
      fetchNotifications();
    };
    
    // Đăng ký event listener
    window.addEventListener('notificationTrigger', handleNotificationTrigger);
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('notificationTrigger', handleNotificationTrigger);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const config = token ? {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      } : {};
      console.log('Fetching notifications with config:', config);
      // Use window.AMS_CONFIG for production compatibility
      const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
      const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
      const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
      const response = await axios.get(`${API_BASE_URL}/notifications/unread/`, config);
      // Xử lý dữ liệu phân trang
      if (response.data.results && Array.isArray(response.data.results)) {
        setNotifications(response.data.results);
      } else {
        setNotifications(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      if (err.response && err.response.status === 401) {
        setError('Bạn cần đăng nhập để xem thông báo.');
        setNotifications([]); // Clear notifications on auth error
      } else if (err.response && err.response.status === 403) {
        setError('Bạn không có quyền xem thông báo.');
        setNotifications([]);
      } else {
        setError(`Không thể tải thông báo. Vui lòng thử lại sau.`);
      }
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const config = token ? {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      } : {};
      // Use window.AMS_CONFIG for production compatibility
      const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
      const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
      const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
      await axios.post(`${API_BASE_URL}/notifications/${id}/mark_read/`, {}, config);
      setNotifications(notifications.filter(notification => notification.id !== id));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const config = token ? {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      } : {};
      // Use window.AMS_CONFIG for production compatibility
      const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || 'http://localhost:8000';
      const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';
      const API_BASE_URL = `${BACKEND_API_URL}${API_PATH}`;
      await axios.post(`${API_BASE_URL}/notifications/mark_all_read/`, {}, config);
      setNotifications([]);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const handleRefresh = () => {
    fetchNotifications();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Unread Notifications</Typography>
        <Box>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh} 
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<CheckIcon />} 
            onClick={handleMarkAllRead} 
            disabled={loading || notifications.length === 0}
          >
            Mark All as Read
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Typography>Loading notifications...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : notifications.length === 0 ? (
        <Typography>No unread notifications.</Typography>
      ) : (
        <List>
          {notifications.map(notification => (
            <ListItem key={notification.id} divider>
              <ListItemText
                primary={notification.title}
                secondary={`${notification.message} - ${new Date(notification.created_at).toLocaleString()}`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="mark as read"
                  onClick={() => handleMarkRead(notification.id)}
                  size="small"
                >
                  <CheckIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default NotificationPanel;
